import {
	Button,
	Modal,
	ModalContent,
	ModalDescription,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@ondit/ui";
import { ModalHeader } from "~/core/ui/dialog";
/**
 * What the assistant searched for on the way to its answer.
 *
 * `samples.web_queries` has been collected since the first sampling run and
 * nothing ever read it. It is the closest thing this product has to a keyword
 * report, and it is better than one: these are the assistant's own
 * reformulations of the question, which is what a page actually has to match.
 *
 * Coverage is uneven and the screen says so rather than drawing a zero. A
 * platform that does not report its searches has not "searched nothing", and
 * the two readings lead to opposite decisions about the same page.
 */

import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { api, type FanoutRow } from "~/core/api/index";
import { useT } from "~/core/i18n";
import { keys, read } from "~/core/query";
import { Section } from "~/core/ui/page";
import { Async } from "~/core/ui/states";
import { Bars } from "~/modules/analytics/components/chart";
import { Drill } from "~/modules/analytics/components/detail";
import { type ReadingProps, StageShell } from "~/modules/analytics/components/stage";
import { matches, SortHeader, TableTools, useSort } from "~/modules/analytics/components/table";

type Column = "text" | "queries" | "per_run";

export function InsightFanouts({ entityId, filters, setFilters }: ReadingProps) {
	const t = useT();
	const [query, setQuery] = useState("");
	const [selected, setSelected] = useState<FanoutRow | null>(null);
	const sort = useSort<Column>("queries");

	const state = useQuery({
		queryKey: [
			...keys.entity(entityId),
			"insights",
			"fanouts",
			{
				range: filters.range,
				engine: filters.engine,
				locale: filters.locale,
				topics: filters.topics,
				intent: filters.intent,
			},
		],
		queryFn: () => api.insights.fanouts(entityId, filters),
		...read,
	});

	return (
		<StageShell entityId={entityId} reading="fanouts" filters={filters} setFilters={setFilters}>
			<Async state={state}>
				{(data) => {
					const rows = sort.apply(
						data.prompts.filter((row) => matches(query, row.text, ...row.examples)),
						(row, column) => (column === "text" ? row.text : column === "queries" ? row.queries : row.per_run),
					);

					return (
						<>
							{data.reported === 0 ? (
								<div className="notice" style={{ marginBottom: "var(--s-9)" }}>
									<strong>{t("fanouts.noCoverage")}</strong>
									<p className="muted">{t("fanouts.noCoverageBody")}</p>
								</div>
							) : (
								<p className="faint" style={{ marginBottom: "var(--s-9)" }}>
									{t("fanouts.coverage", { reported: data.reported, total: data.prompts.length })}
								</p>
							)}

							<Section title={t("fanouts.byPlatform")} lede={t("fanouts.byPlatformHint")}>
								<div className="metric-panel">
									<div className="metric-figure">
										<Bars
											metric="average_position"
											rows={data.platforms.map((platform) => ({
												key: platform.engine,
												label: t(`engine.${platform.engine}`),
												value: platform.per_run,
											}))}
										/>
									</div>
									<div className="metric-rank">
										<Table variant="resource" className="rank-table">
											<TableHeader>
												<TableRow>
													<TableHead className="wrap">{t("filter.engine")}</TableHead>
													<TableHead className="num">{t("fanouts.perRun")}</TableHead>
												</TableRow>
											</TableHeader>
											<TableBody>
												{data.platforms.length === 0 ? (
													<TableRow>
														<TableCell className="wrap faint" colSpan={2}>
															{t("insights.noData")}
														</TableCell>
													</TableRow>
												) : (
													data.platforms.map((platform) => (
														<TableRow key={platform.engine}>
															<TableCell className="wrap">
																<span className="rank-place num">{platform.rank ?? "—"}</span>
																{t(`engine.${platform.engine}`)}
															</TableCell>
															<TableCell className="num mono">{platform.per_run?.toFixed(1) ?? "—"}</TableCell>
														</TableRow>
													))
												)}
											</TableBody>
										</Table>
									</div>
								</div>
							</Section>

							<Section title={t("fanouts.byPrompt")} lede={t("fanouts.byPromptHint")}>
								<TableTools
									sort={sort.config([
										{ id: "text", label: t("visibility.question") },
										{ id: "queries", label: t("fanouts.queries") },
										{ id: "per_run", label: t("fanouts.perRun") },
									])}
									query={query}
									onQuery={setQuery}
									placeholder={t("visibility.question")}
									shown={rows.length}
									total={data.prompts.length}
									csv={{ name: `fanouts-${filters.range}`, rows: csv(rows) }}
								/>

								{rows.length === 0 ? (
									<p className="empty">{t("table.noMatch")}</p>
								) : (
									<div className="table-scroll">
										<Table variant="resource">
											<TableHeader>
												<TableRow>
													<SortHeader className="wrap" label={t("visibility.question")} column="text" sort={sort} />
													<SortHeader className="num" label={t("fanouts.queries")} column="queries" sort={sort} />
													<SortHeader className="num" label={t("fanouts.perRun")} column="per_run" sort={sort} />
												</TableRow>
											</TableHeader>
											<TableBody>
												{rows.map((row) => (
													<TableRow key={row.id}>
														<TableCell className="wrap">
															<Drill view={{ kind: "answers", label: row.text, filters: { question: row.id } }}>
																{row.text}
															</Drill>
															{row.examples.length > 0 ? (
																<div className="fanout-summary">
																	<span className="faint">{row.examples[0]}</span>
																	<Button variant="default" onClick={() => setSelected(row)}>
																		{t("fanouts.viewQueries", { n: row.examples.length })}
																	</Button>
																</div>
															) : null}
														</TableCell>
														<TableCell className="num mono">{row.queries}</TableCell>
														<TableCell className="num mono">{row.per_run?.toFixed(1) ?? "—"}</TableCell>
													</TableRow>
												))}
											</TableBody>
										</Table>
									</div>
								)}
							</Section>
						</>
					);
				}}
			</Async>
			<Modal
				open={selected !== null}
				onOpenChange={(open) => {
					if (!open) setSelected(null);
				}}
			>
				<ModalContent className="fanout-detail">
					<ModalHeader>{t("fanouts.byPrompt")}</ModalHeader>
					<ModalDescription>{selected?.text}</ModalDescription>
					<ul className="fanout-examples">
						{selected?.examples.map((example) => (
							<li key={example}>{example}</li>
						))}
					</ul>
				</ModalContent>
			</Modal>
		</StageShell>
	);
}

function csv(rows: FanoutRow[]): (string | number)[][] {
	return [
		["prompt", "topic", "queries", "runs", "queries_per_run", "examples"],
		...rows.map((row) => [
			row.text,
			row.topic,
			row.queries,
			row.runs,
			row.per_run?.toFixed(2) ?? "",
			row.examples.join(" | "),
		]),
	];
}
